import { ReactElement } from 'react';
import { styled } from '@mui/material/styles';
import { closestCenter, DndContext, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext } from '@dnd-kit/sortable';
import { ViewModel } from '../../../../models/view.model';
import { CHART_COMPONENTS } from '../../../../types';
import { useView } from '../../hooks/useView';
import { ChartComponent } from './ChartComponent';

const ChartContainer = styled('div')`
  display: grid;
  grid-template-columns: repeat(auto-fit, 24.25%);
  column-gap: 1%; // 1% to keep the same units as for cols - ensures we don't go over 100%
  row-gap: 1rem;
`;

interface IChartsListProps {
  sectionId: string;
  view: ViewModel;
  disableDrag?: boolean;
}

export const ChartsList = ({ sectionId, view, disableDrag = false }: IChartsListProps): ReactElement => {
  const { getComponentsOrder, setComponentsOrder } = useView();
  const selectedCharts = getComponentsOrder(sectionId) as CHART_COMPONENTS[];

  const handleReorder = (e: DragEndEvent) => {
    if (disableDrag) {
      return;
    }

    const { active, over } = e;
    if (active.id !== over?.id) {
      const oldIndex = selectedCharts.indexOf(active.id as CHART_COMPONENTS);
      const newIndex = selectedCharts.indexOf(over?.id as CHART_COMPONENTS);
      const reOrderedCharts = arrayMove(selectedCharts, oldIndex, newIndex);
      setComponentsOrder(sectionId, reOrderedCharts);
    }
  };

  // If drag is disabled, render without DndContext
  if (disableDrag) {
    return (
      <ChartContainer>
        {selectedCharts.map((chartId) => (
          <ChartComponent key={chartId} id={chartId} view={view} />
        ))}
      </ChartContainer>
    );
  }

  return (
    <ChartContainer>
      <DndContext collisionDetection={closestCenter} onDragEnd={handleReorder}>
        <SortableContext items={selectedCharts}>
          {selectedCharts.map((chartId) => (
            <ChartComponent key={chartId} id={chartId} view={view} />
          ))}
        </SortableContext>
      </DndContext>
    </ChartContainer>
  );
};
